import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  ClipboardList,
  ClipboardPlus,
  FileEdit,
  FlaskConical,
  Mic,
  Paperclip,
  Pill,
  ScanEye,
  Stethoscope,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { hasPermission } from '@/modules/auth/useAuth';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

import { AnamnesisSection } from './AnamnesisSection';
import { AttachmentsSection } from './AttachmentsSection';
import { ClinicalBottomActionBar } from './ClinicalBottomActionBar';
import { ClinicalHistoryModal } from './ClinicalHistoryModal';
import { ClinicalRecordHeader } from './ClinicalRecordHeader';
import { ClinicalSectionStepper, type StepperItem } from './ClinicalSectionStepper';
import { ClinicalSummaryPanel } from './ClinicalSummaryPanel';
import {
  examHasContentFrontend,
  formatClinicalSummaryValues,
  resolveAddendaStatus,
  resolveAnamnesisStatus,
  resolveDiagnosesStatus,
  resolveExamStatus,
  resolvePlanStatus,
} from './clinical-status';
import { printClinicalRecord } from './clinical-record-print';
import { CloseVisitValidationModal } from './CloseVisitValidationModal';
import { DiagnosesSection, type DiagnosisUpdatePatch } from './DiagnosesSection';
import { ImagingSection } from './ImagingSection';
import { LabsSection } from './LabsSection';
import { PhysicalExamSection } from './PhysicalExamSection';
import { PlanTreatmentSection } from './PlanTreatmentSection';
import { PrescriptionsSection } from './PrescriptionsSection';
import { stringifyPatch, VoiceFillSection } from './VoiceFillSection';
import { formatWeightLabel, useRxPrintBase } from './useRxPrintBase';
import type { VoiceNoteSuggestions } from './voice-notes-rest';

import {
  ADD_CLINICAL_ADDENDUM_MUTATION,
  ADD_CLINICAL_DIAGNOSIS_MUTATION,
  CLINICAL_ATTACHMENTS_QUERY,
  CLINICAL_IMAGING_STUDIES_QUERY,
  CLINICAL_LAB_REQUESTS_QUERY,
  CLINICAL_PRESCRIPTIONS_QUERY,
  CLINICAL_VISIT_BY_ORDER_QUERY,
  CLINICAL_VISIT_DETAIL_QUERY,
  CLOSE_CLINICAL_VISIT_MUTATION,
  PET_CLINICAL_SUMMARY_QUERY,
  REMOVE_CLINICAL_DIAGNOSIS_MUTATION,
  SAVE_CLINICAL_ANAMNESIS_MUTATION,
  SAVE_CLINICAL_PHYSICAL_EXAM_MUTATION,
  SAVE_CLINICAL_PLAN_TREATMENT_MUTATION,
  UPDATE_CLINICAL_DIAGNOSIS_MUTATION,
  idempotencyContext,
} from './clinical.gql';

const LOCKED = new Set(['closed', 'addended', 'cancelled', 'voided']);
const ADDENDABLE = new Set(['closed', 'addended']);
const TABS = [
  'voice',
  'anamnesis',
  'exam',
  'diagnoses',
  'plan',
  'prescriptions',
  'labs',
  'imaging',
  'attachments',
  'addendums',
] as const;
type Tab = (typeof TABS)[number];

const TAB_ICONS: Record<Tab, StepperItem['icon']> = {
  voice: Mic,
  anamnesis: ClipboardList,
  exam: Stethoscope,
  diagnoses: ClipboardPlus,
  plan: FileEdit,
  prescriptions: Pill,
  labs: FlaskConical,
  imaging: ScanEye,
  attachments: Paperclip,
  addendums: FileEdit,
};

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */

const KNOWN_ERROR_CODES = [
  'clinical_visit_locked',
  'clinical_visit_close_requires_diagnosis',
  'clinical_visit_close_requires_exam',
  'clinical_addendum_visit_not_closed',
  'clinical_addendum_empty',
  'clinical_diagnoses_reorder_id_mismatch',
  'clinical_visit_not_closeable',
  'clinical_visit_not_pendable',
  'clinical_visit_not_found',
  'clinical_diagnosis_not_found',
];

function errText(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApolloError) {
    const raw = err.graphQLErrors[0]?.message ?? err.message;
    // Service codes often carry a `:<suffix>` (e.g. clinical_visit_not_closeable:draft).
    const base = raw.split(':')[0] ?? raw;
    if (KNOWN_ERROR_CODES.includes(base)) return t(`clinical.error_${base}`);
    return raw;
  }
  if (err instanceof Error) return err.message;
  return 'error';
}

export function ClinicalVisitPanel({
  sale_order_id,
  visit_id,
}: {
  sale_order_id?: string;
  visit_id?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const perms = useAuthStore((s) => s.user?.permissions ?? []);
  const can_write = hasPermission(perms, 'clinical.visits.write');
  const can_close = hasPermission(perms, 'clinical.visits.close');
  const can_diag = hasPermission(perms, 'clinical.diagnoses.write');
  const can_addendum = hasPermission(perms, 'clinical.visits.addendum');
  const can_attach = hasPermission(perms, 'clinical.attachments.write');
  const can_labs = hasPermission(perms, 'clinical.labs.write');
  const can_imaging = hasPermission(perms, 'clinical.imaging.write');
  const can_rx = hasPermission(perms, 'clinical.prescriptions.write');
  const can_voice = hasPermission(perms, 'clinical.voice.transcribe');

  // Two entry points share this panel: the doctor-POS draft (keyed by sale_order_id) and
  // the clinical-records "pending" list (keyed by visit_id directly, e.g. resuming a visit
  // later in the day with no active draft). Exactly one of the two props is set.
  const { data: by_order_data, refetch: refetch_by_order } = useQuery(CLINICAL_VISIT_BY_ORDER_QUERY, {
    variables: { sale_order_id: sale_order_id ?? '' },
    skip: !sale_order_id,
    fetchPolicy: 'cache-and-network',
  });
  const { data: by_id_data, refetch: refetch_by_id } = useQuery(CLINICAL_VISIT_DETAIL_QUERY, {
    variables: { id: visit_id ?? '' },
    skip: !visit_id,
    fetchPolicy: 'cache-and-network',
  });
  const data = sale_order_id ? by_order_data : by_id_data;
  const refetch = sale_order_id ? refetch_by_order : refetch_by_id;
  const detail = sale_order_id ? (data?.clinicalVisitByOrderId ?? null) : (data?.clinicalVisit ?? null);
  const visit = detail?.visit ?? null;
  const locked = visit ? LOCKED.has(visit.status) : true;
  // Attachments/results may be appended on closed/addended visits — only cancelled/voided block.
  const appendable = visit ? !['cancelled', 'voided'].includes(visit.status) : false;
  const editable = Boolean(visit) && !locked && can_write;

  const { data: summary_data } = useQuery(PET_CLINICAL_SUMMARY_QUERY, {
    variables: { pet_id: visit?.pet_id ?? '' },
    skip: !visit?.pet_id,
    fetchPolicy: 'cache-and-network',
  });
  const summary = summary_data?.petClinicalSummary ?? null;

  // Count-only reads reusing the exact queries each sibling section already runs —
  // shares Apollo's cache entry with them instead of adding a new backend surface.
  const { data: rx_count_data } = useQuery(CLINICAL_PRESCRIPTIONS_QUERY, {
    variables: { visit_id: visit?.id ?? '' },
    skip: !visit?.id,
    fetchPolicy: 'cache-first',
  });
  const { data: labs_count_data } = useQuery(CLINICAL_LAB_REQUESTS_QUERY, {
    variables: { visit_id: visit?.id ?? '' },
    skip: !visit?.id,
    fetchPolicy: 'cache-first',
  });
  const { data: imaging_count_data } = useQuery(CLINICAL_IMAGING_STUDIES_QUERY, {
    variables: { visit_id: visit?.id ?? '' },
    skip: !visit?.id,
    fetchPolicy: 'cache-first',
  });
  const { data: attachments_count_data } = useQuery(CLINICAL_ATTACHMENTS_QUERY, {
    variables: { visit_id: visit?.id ?? '' },
    skip: !visit?.id,
    fetchPolicy: 'cache-first',
  });
  const prescriptions_active_count = (rx_count_data?.clinicalPrescriptions ?? []).filter(
    (rx: any) => rx.status === 'active',
  ).length;
  const studies_count =
    (labs_count_data?.clinicalLabRequests ?? []).length +
    (imaging_count_data?.clinicalImagingStudies ?? []).length;
  const attachments_count = (attachments_count_data?.clinicalAttachments ?? []).length;

  const rx_print_info = useRxPrintBase({
    pet_id: visit?.pet_id ?? null,
    veterinarian_id: visit?.veterinarian_id ?? null,
    pet_name: summary?.pet_name ?? null,
    species: summary?.species ?? null,
    pet_record_code: summary?.pet_record_code ?? null,
  });
  const rx_print_base = {
    ...rx_print_info,
    visit_folio: visit?.folio_number ?? '—',
    weight_label: formatWeightLabel(
      detail?.physical_exam?.body_weight_grams ?? visit?.weight_grams_at_visit ?? null,
    ),
  };
  // Shared "patient" header for the multi-section documents (expediente, órdenes de
  // imagenología/laboratorio) — same underlying data as `rx_print_base` above, plus the
  // visit date every one of those documents shows next to "Tutor/Propietario".
  const clinical_print_patient = {
    ...rx_print_base,
    visit_date: visit ? new Date(visit.created_at).toLocaleDateString() : '—',
  };
  const primary_diagnosis =
    (detail?.diagnoses ?? []).find((d: any) => d.is_primary)?.description ?? null;

  const [tab, setTab] = useState<Tab>('anamnesis');
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeModalError, setCloseModalError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [savingAll, setSavingAll] = useState(false);

  // Section form state, hydrated from the loaded detail.
  const [anam, setAnam] = useState<Record<string, string>>({});
  const [exam, setExam] = useState<Record<string, string>>({});
  const [plan, setPlan] = useState<Record<string, string>>({});
  const [closeReasons, setCloseReasons] = useState({ diag: '', exam: '' });
  const [newDiag, setNewDiag] = useState({ kind: 'presumptive', description: '', is_primary: false });
  const [addendum, setAddendum] = useState('');

  // Hydrate the section forms once per visit. A save triggers refetch() which would
  // otherwise re-run this and clobber unsaved edits in a non-active tab. Status,
  // diagnoses and addenda still refresh on every render (they read `detail` directly).
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!detail || !visit) return;
    if (hydratedFor.current === visit.id) return;
    hydratedFor.current = visit.id;
    const a = detail.anamnesis ?? {};
    setAnam({
      chief_complaint: a.chief_complaint ?? '',
      symptom_onset_days: a.symptom_onset_days != null ? String(a.symptom_onset_days) : '',
      previous_illnesses: a.previous_illnesses ?? '',
      current_medications: a.current_medications ?? '',
      vaccination_status: a.vaccination_status ?? '',
      deworming_status: a.deworming_status ?? '',
      diet_notes: a.diet_notes ?? '',
      environment_notes: a.environment_notes ?? '',
      other_notes: a.other_notes ?? '',
      surgeries_notes: a.surgeries_notes ?? '',
      last_heat_notes: a.last_heat_notes ?? '',
      barf_diet_notes: a.barf_diet_notes ?? '',
      fleas_ticks_notes: a.fleas_ticks_notes ?? '',
      cohabitation_notes: a.cohabitation_notes ?? '',
      street_exposure_notes: a.street_exposure_notes ?? '',
    });
    const e = detail.physical_exam ?? {};
    const fallback_weight = detail.visit?.weight_grams_at_visit ?? null;
    setExam({
      body_weight_grams:
        e.body_weight_grams != null
          ? String(e.body_weight_grams)
          : fallback_weight != null
            ? String(fallback_weight)
            : '',
      body_temperature_celsius_tenths:
        e.body_temperature_celsius_tenths != null ? String(e.body_temperature_celsius_tenths) : '',
      heart_rate_bpm: e.heart_rate_bpm != null ? String(e.heart_rate_bpm) : '',
      respiratory_rate_bpm: e.respiratory_rate_bpm != null ? String(e.respiratory_rate_bpm) : '',
      body_condition_score: e.body_condition_score != null ? String(e.body_condition_score) : '',
      mucous_membranes: e.mucous_membranes ?? '',
      hydration_status: e.hydration_status ?? '',
      consciousness_level: e.consciousness_level ?? '',
      cardiovascular_notes: e.cardiovascular_notes ?? '',
      respiratory_notes: e.respiratory_notes ?? '',
      gastrointestinal_notes: e.gastrointestinal_notes ?? '',
      abdomen_notes: e.abdomen_notes ?? '',
      lymph_node_notes: e.lymph_node_notes ?? '',
      capillary_refill_time: e.capillary_refill_time ?? '',
      cough_reflex: e.cough_reflex ?? '',
      gag_reflex: e.gag_reflex ?? '',
      pulse_description: e.pulse_description ?? '',
      auscultation_percussion_notes: e.auscultation_percussion_notes ?? '',
      general_impression: e.general_impression ?? '',
      exam_not_performed_reason: e.exam_not_performed_reason ?? '',
    });
    const p = detail.plan_treatment ?? {};
    setPlan({
      treatment_notes: p.treatment_notes ?? '',
      medications_notes: p.medications_notes ?? '',
      diet_recommendations: p.diet_recommendations ?? '',
      activity_restrictions: p.activity_restrictions ?? '',
      follow_up_notes: p.follow_up_notes ?? '',
      owner_instructions: p.owner_instructions ?? '',
    });
  }, [detail]);

  const [saveAnam] = useMutation(SAVE_CLINICAL_ANAMNESIS_MUTATION);
  const [saveExam] = useMutation(SAVE_CLINICAL_PHYSICAL_EXAM_MUTATION);
  const [savePlan] = useMutation(SAVE_CLINICAL_PLAN_TREATMENT_MUTATION);
  const [addDiag] = useMutation(ADD_CLINICAL_DIAGNOSIS_MUTATION);
  const [updateDiag] = useMutation(UPDATE_CLINICAL_DIAGNOSIS_MUTATION);
  const [removeDiag] = useMutation(REMOVE_CLINICAL_DIAGNOSIS_MUTATION);
  const [closeVisit, { loading: closing }] = useMutation(CLOSE_CLINICAL_VISIT_MUTATION);
  const [addAddendum] = useMutation(ADD_CLINICAL_ADDENDUM_MUTATION);

  const num = (v: string | undefined): number | undefined => {
    if (v == null) return undefined;
    const n = Number(v);
    return v.trim() === '' || Number.isNaN(n) ? undefined : n;
  };
  const txt = (v: string | undefined): string | undefined =>
    v == null || v.trim() === '' ? undefined : v;

  const run = async (fn: () => Promise<unknown>, successTitle?: string): Promise<void> => {
    try {
      setError(null);
      await fn();
      await refetch();
      setLastSavedAt(Date.now());
      // showToast dedupes by tone+title, so repeated save-on-blur confirmations collapse
      // to a single toast rather than spamming as the doctor tabs between fields.
      if (successTitle) showToast({ tone: 'success', title: successTitle });
    } catch (err) {
      setError(errText(err, t));
    }
  };

  if (!sale_order_id && !visit_id) return null;
  if (!visit) {
    return (
      <Card>
        <CardContent className="text-muted-foreground p-4 text-sm">
          {t('clinical.no_visit')}
        </CardContent>
      </Card>
    );
  }

  const onSaveAnamnesis = (): Promise<void> =>
    run(() =>
      saveAnam({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            chief_complaint: txt(anam.chief_complaint),
            symptom_onset_days: num(anam.symptom_onset_days),
            previous_illnesses: txt(anam.previous_illnesses),
            current_medications: txt(anam.current_medications),
            vaccination_status: txt(anam.vaccination_status),
            deworming_status: txt(anam.deworming_status),
            diet_notes: txt(anam.diet_notes),
            environment_notes: txt(anam.environment_notes),
            other_notes: txt(anam.other_notes),
            surgeries_notes: txt(anam.surgeries_notes),
            last_heat_notes: txt(anam.last_heat_notes),
            barf_diet_notes: txt(anam.barf_diet_notes),
            fleas_ticks_notes: txt(anam.fleas_ticks_notes),
            cohabitation_notes: txt(anam.cohabitation_notes),
            street_exposure_notes: txt(anam.street_exposure_notes),
          },
        },
        context: idempotencyContext(),
      }),
      t('clinical.toast_section_saved'),
    );

  const onSaveExam = (): Promise<void> =>
    run(() =>
      saveExam({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            body_weight_grams: num(exam.body_weight_grams),
            body_temperature_celsius_tenths: num(exam.body_temperature_celsius_tenths),
            heart_rate_bpm: num(exam.heart_rate_bpm),
            respiratory_rate_bpm: num(exam.respiratory_rate_bpm),
            body_condition_score: num(exam.body_condition_score),
            mucous_membranes: txt(exam.mucous_membranes),
            hydration_status: txt(exam.hydration_status),
            consciousness_level: txt(exam.consciousness_level),
            cardiovascular_notes: txt(exam.cardiovascular_notes),
            respiratory_notes: txt(exam.respiratory_notes),
            gastrointestinal_notes: txt(exam.gastrointestinal_notes),
            abdomen_notes: txt(exam.abdomen_notes),
            lymph_node_notes: txt(exam.lymph_node_notes),
            capillary_refill_time: txt(exam.capillary_refill_time),
            cough_reflex: txt(exam.cough_reflex),
            gag_reflex: txt(exam.gag_reflex),
            pulse_description: txt(exam.pulse_description),
            auscultation_percussion_notes: txt(exam.auscultation_percussion_notes),
            general_impression: txt(exam.general_impression),
            exam_not_performed_reason: txt(exam.exam_not_performed_reason),
          },
        },
        context: idempotencyContext(),
      }),
      t('clinical.toast_section_saved'),
    );

  const onSavePlan = (): Promise<void> =>
    run(() =>
      savePlan({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            treatment_notes: txt(plan.treatment_notes),
            medications_notes: txt(plan.medications_notes),
            diet_recommendations: txt(plan.diet_recommendations),
            activity_restrictions: txt(plan.activity_restrictions),
            follow_up_notes: txt(plan.follow_up_notes),
            owner_instructions: txt(plan.owner_instructions),
          },
        },
        context: idempotencyContext(),
      }),
      t('clinical.toast_section_saved'),
    );

  const onSaveAll = async (): Promise<void> => {
    setSavingAll(true);
    try {
      setError(null);
      await Promise.all([
        saveAnam({
          variables: {
            input: {
              clinical_visit_id: visit.id,
              chief_complaint: txt(anam.chief_complaint),
              symptom_onset_days: num(anam.symptom_onset_days),
              previous_illnesses: txt(anam.previous_illnesses),
              current_medications: txt(anam.current_medications),
              vaccination_status: txt(anam.vaccination_status),
              deworming_status: txt(anam.deworming_status),
              diet_notes: txt(anam.diet_notes),
              environment_notes: txt(anam.environment_notes),
              other_notes: txt(anam.other_notes),
              surgeries_notes: txt(anam.surgeries_notes),
              last_heat_notes: txt(anam.last_heat_notes),
              barf_diet_notes: txt(anam.barf_diet_notes),
              fleas_ticks_notes: txt(anam.fleas_ticks_notes),
              cohabitation_notes: txt(anam.cohabitation_notes),
              street_exposure_notes: txt(anam.street_exposure_notes),
            },
          },
          context: idempotencyContext(),
        }),
        saveExam({
          variables: {
            input: {
              clinical_visit_id: visit.id,
              body_weight_grams: num(exam.body_weight_grams),
              body_temperature_celsius_tenths: num(exam.body_temperature_celsius_tenths),
              heart_rate_bpm: num(exam.heart_rate_bpm),
              respiratory_rate_bpm: num(exam.respiratory_rate_bpm),
              body_condition_score: num(exam.body_condition_score),
              mucous_membranes: txt(exam.mucous_membranes),
              hydration_status: txt(exam.hydration_status),
              consciousness_level: txt(exam.consciousness_level),
              cardiovascular_notes: txt(exam.cardiovascular_notes),
              respiratory_notes: txt(exam.respiratory_notes),
              gastrointestinal_notes: txt(exam.gastrointestinal_notes),
              abdomen_notes: txt(exam.abdomen_notes),
              lymph_node_notes: txt(exam.lymph_node_notes),
              capillary_refill_time: txt(exam.capillary_refill_time),
              cough_reflex: txt(exam.cough_reflex),
              gag_reflex: txt(exam.gag_reflex),
              pulse_description: txt(exam.pulse_description),
              auscultation_percussion_notes: txt(exam.auscultation_percussion_notes),
              general_impression: txt(exam.general_impression),
              exam_not_performed_reason: txt(exam.exam_not_performed_reason),
            },
          },
          context: idempotencyContext(),
        }),
        savePlan({
          variables: {
            input: {
              clinical_visit_id: visit.id,
              treatment_notes: txt(plan.treatment_notes),
              medications_notes: txt(plan.medications_notes),
              diet_recommendations: txt(plan.diet_recommendations),
              activity_restrictions: txt(plan.activity_restrictions),
              follow_up_notes: txt(plan.follow_up_notes),
              owner_instructions: txt(plan.owner_instructions),
            },
          },
          context: idempotencyContext(),
        }),
      ]);
      await refetch();
      setLastSavedAt(Date.now());
      showToast({ tone: 'success', title: t('clinical.toast_all_saved') });
    } catch (err) {
      setError(errText(err, t));
    } finally {
      setSavingAll(false);
    }
  };

  // One-click counterpart to the per-section "Usar sugerencias" buttons: merges every
  // suggested field into the 3 form sections + persists them, and directly creates each
  // suggested diagnosis (bypassing the single `newDiag` draft, since there may be several).
  // Returns false (without throwing) on failure so the caller skips marking the note applied.
  const onApplyAndSaveAllVoiceSuggestions = async (s: VoiceNoteSuggestions): Promise<boolean> => {
    setSavingAll(true);
    try {
      setError(null);
      const merged_anam = { ...anam, ...stringifyPatch(s.anamnesis) };
      const merged_exam = { ...exam, ...stringifyPatch(s.physical_exam) };
      const merged_plan = { ...plan, ...stringifyPatch(s.plan) };
      setAnam(merged_anam);
      setExam(merged_exam);
      setPlan(merged_plan);

      await Promise.all([
        saveAnam({
          variables: {
            input: {
              clinical_visit_id: visit.id,
              chief_complaint: txt(merged_anam.chief_complaint),
              symptom_onset_days: num(merged_anam.symptom_onset_days),
              previous_illnesses: txt(merged_anam.previous_illnesses),
              current_medications: txt(merged_anam.current_medications),
              vaccination_status: txt(merged_anam.vaccination_status),
              deworming_status: txt(merged_anam.deworming_status),
              diet_notes: txt(merged_anam.diet_notes),
              environment_notes: txt(merged_anam.environment_notes),
              other_notes: txt(merged_anam.other_notes),
              surgeries_notes: txt(merged_anam.surgeries_notes),
              last_heat_notes: txt(merged_anam.last_heat_notes),
              barf_diet_notes: txt(merged_anam.barf_diet_notes),
              fleas_ticks_notes: txt(merged_anam.fleas_ticks_notes),
              cohabitation_notes: txt(merged_anam.cohabitation_notes),
              street_exposure_notes: txt(merged_anam.street_exposure_notes),
            },
          },
          context: idempotencyContext(),
        }),
        saveExam({
          variables: {
            input: {
              clinical_visit_id: visit.id,
              body_weight_grams: num(merged_exam.body_weight_grams),
              body_temperature_celsius_tenths: num(merged_exam.body_temperature_celsius_tenths),
              heart_rate_bpm: num(merged_exam.heart_rate_bpm),
              respiratory_rate_bpm: num(merged_exam.respiratory_rate_bpm),
              body_condition_score: num(merged_exam.body_condition_score),
              mucous_membranes: txt(merged_exam.mucous_membranes),
              hydration_status: txt(merged_exam.hydration_status),
              consciousness_level: txt(merged_exam.consciousness_level),
              cardiovascular_notes: txt(merged_exam.cardiovascular_notes),
              respiratory_notes: txt(merged_exam.respiratory_notes),
              gastrointestinal_notes: txt(merged_exam.gastrointestinal_notes),
              abdomen_notes: txt(merged_exam.abdomen_notes),
              lymph_node_notes: txt(merged_exam.lymph_node_notes),
              capillary_refill_time: txt(merged_exam.capillary_refill_time),
              cough_reflex: txt(merged_exam.cough_reflex),
              gag_reflex: txt(merged_exam.gag_reflex),
              pulse_description: txt(merged_exam.pulse_description),
              auscultation_percussion_notes: txt(merged_exam.auscultation_percussion_notes),
              general_impression: txt(merged_exam.general_impression),
              exam_not_performed_reason: txt(merged_exam.exam_not_performed_reason),
            },
          },
          context: idempotencyContext(),
        }),
        savePlan({
          variables: {
            input: {
              clinical_visit_id: visit.id,
              treatment_notes: txt(merged_plan.treatment_notes),
              medications_notes: txt(merged_plan.medications_notes),
              diet_recommendations: txt(merged_plan.diet_recommendations),
              activity_restrictions: txt(merged_plan.activity_restrictions),
              follow_up_notes: txt(merged_plan.follow_up_notes),
              owner_instructions: txt(merged_plan.owner_instructions),
            },
          },
          context: idempotencyContext(),
        }),
        ...s.diagnoses.map((d) =>
          addDiag({
            variables: {
              input: {
                clinical_visit_id: visit.id,
                diagnosis_kind: d.diagnosis_kind,
                description: d.description,
                is_primary: d.is_primary,
              },
            },
            context: idempotencyContext(),
          }),
        ),
      ]);

      await refetch();
      setLastSavedAt(Date.now());
      showToast({ tone: 'success', title: t('clinical.voice_toast_all_applied') });
      return true;
    } catch (err) {
      setError(errText(err, t));
      return false;
    } finally {
      setSavingAll(false);
    }
  };

  const onDownloadRecord = async (): Promise<void> => {
    if (!visit) return;
    const ok = await printClinicalRecord(
      {
        patient: clinical_print_patient,
        anamnesis: detail?.anamnesis ?? null,
        exam: detail?.physical_exam ?? null,
        diagnoses: detail?.diagnoses ?? [],
        plan: detail?.plan_treatment ?? null,
        addendums: detail?.addendums ?? [],
      },
      t,
    );
    if (!ok) showToast({ tone: 'error', title: t('clinical.record_print_blocked') });
  };

  const onAddDiagnosis = (): Promise<void> => {
    // Validate before run() so a rejected input never shows a success toast.
    if (newDiag.description.trim().length < 3) {
      setError(t('clinical.error_diagnosis_short'));
      return Promise.resolve();
    }
    return run(async () => {
      await addDiag({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            diagnosis_kind: newDiag.kind,
            description: newDiag.description,
            is_primary: newDiag.is_primary,
          },
        },
        context: idempotencyContext(),
      });
      setNewDiag({ kind: 'presumptive', description: '', is_primary: false });
    }, t('clinical.toast_diagnosis_added'));
  };

  const onUpdateDiagnosis = (id: string, patch: DiagnosisUpdatePatch): Promise<void> =>
    run(
      () => updateDiag({ variables: { id, input: patch }, context: idempotencyContext() }),
      t('clinical.toast_section_saved'),
    );

  const onRemoveDiagnosis = (id: string): Promise<void> =>
    run(
      () => removeDiag({ variables: { id }, context: idempotencyContext() }),
      t('clinical.toast_diagnosis_removed'),
    );

  const onAddAddendum = (): Promise<void> =>
    run(async () => {
      await addAddendum({
        variables: { input: { clinical_visit_id: visit.id, content: addendum } },
        context: idempotencyContext(),
      });
      setAddendum('');
    }, t('clinical.toast_addendum_added'));

  const onConfirmClose = async (): Promise<void> => {
    setCloseModalError(null);
    try {
      await closeVisit({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            has_no_diagnosis_reason: txt(closeReasons.diag),
            exam_not_performed_reason: txt(closeReasons.exam),
          },
        },
        context: idempotencyContext(),
      });
      await refetch();
      setLastSavedAt(Date.now());
      showToast({ tone: 'success', title: t('clinical.toast_visit_closed') });
      setShowCloseModal(false);
    } catch (err) {
      setCloseModalError(errText(err, t));
    }
  };

  const diagnoses: any[] = detail.diagnoses ?? [];
  const addendums: any[] = detail.addendums ?? [];
  const diagnoses_status = resolveDiagnosesStatus(diagnoses.length);
  const anamnesis_status = resolveAnamnesisStatus(anam);
  const exam_status = resolveExamStatus(exam);
  const plan_status = resolvePlanStatus(plan);
  const addenda_status = resolveAddendaStatus(ADDENDABLE.has(visit.status), addendums.length);
  const exam_complete = examHasContentFrontend(exam);
  const summary_values = formatClinicalSummaryValues(exam, plan);

  const stepper_items: StepperItem[] = [
    // Skald voice dictation is restricted to administrators + veterinarians (medical staff)
    // — the tab is hidden entirely for other roles rather than shown disabled/empty.
    ...(can_voice
      ? [{ key: 'voice', label: t('clinical.tab_voice'), icon: TAB_ICONS.voice, status: 'optional' as const }]
      : []),
    { key: 'anamnesis', label: t('clinical.tab_anamnesis'), icon: TAB_ICONS.anamnesis, status: anamnesis_status },
    { key: 'exam', label: t('clinical.tab_exam'), icon: TAB_ICONS.exam, status: exam_status },
    { key: 'diagnoses', label: t('clinical.tab_diagnoses'), icon: TAB_ICONS.diagnoses, status: diagnoses_status },
    { key: 'plan', label: t('clinical.tab_plan'), icon: TAB_ICONS.plan, status: plan_status },
    { key: 'prescriptions', label: t('clinical.tab_prescriptions'), icon: TAB_ICONS.prescriptions, status: 'optional' },
    { key: 'labs', label: t('clinical.tab_labs'), icon: TAB_ICONS.labs, status: 'optional' },
    { key: 'imaging', label: t('clinical.tab_imaging'), icon: TAB_ICONS.imaging, status: 'optional' },
    { key: 'attachments', label: t('clinical.tab_attachments'), icon: TAB_ICONS.attachments, status: 'optional' },
    { key: 'addendums', label: t('clinical.tab_addendums'), icon: TAB_ICONS.addendums, status: addenda_status },
  ];

  const section_save_map: Partial<Record<Tab, () => Promise<void>>> = {
    anamnesis: onSaveAnamnesis,
    exam: onSaveExam,
    plan: onSavePlan,
  };
  const active_section_save = section_save_map[tab] ?? null;
  const onSaveAndContinue = active_section_save
    ? async (): Promise<void> => {
        await active_section_save();
        const idx = TABS.indexOf(tab);
        const next = TABS[idx + 1];
        if (next) setTab(next);
      }
    : null;

  return (
    <Card>
      <ClinicalRecordHeader
        folio_number={visit.folio_number}
        status={visit.status}
        pet_id={visit.pet_id}
        pet_name={summary?.pet_name ?? null}
        species={summary?.species ?? null}
        visit_count={summary?.visit_count ?? 0}
        open_visit_count={summary?.open_visit_count ?? 0}
        show_close={['in_progress', 'pending_completion'].includes(visit.status) && can_close}
        saving_all={savingAll}
        on_view_record={() => setShowHistory(true)}
        on_download_record={() => void onDownloadRecord()}
        on_save_all={() => void onSaveAll()}
        on_request_close={() => setShowCloseModal(true)}
      />
      <CardContent className="space-y-3 p-4">
        {locked ? (
          <p className="text-muted-foreground text-xs italic">{t('clinical.read_only_hint')}</p>
        ) : null}

        <ClinicalSectionStepper items={stepper_items} active={tab} onSelect={(k) => setTab(k as Tab)} />

        {error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
            {error}
          </div>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="min-w-0">
            {tab === 'voice' ? (
              <VoiceFillSection
                visitId={visit.id}
                canUse={can_voice}
                writable={appendable}
                onApplyAnamnesis={(patch) => {
                  setAnam({ ...anam, ...patch });
                  setTab('anamnesis');
                }}
                onApplyExam={(patch) => {
                  setExam({ ...exam, ...patch });
                  setTab('exam');
                }}
                onApplyPlan={(patch) => {
                  setPlan({ ...plan, ...patch });
                  setTab('plan');
                }}
                onApplyDiagnosis={(d) => {
                  setNewDiag({ kind: d.diagnosis_kind, description: d.description, is_primary: d.is_primary });
                  setTab('diagnoses');
                }}
                onApplyAndSaveAll={onApplyAndSaveAllVoiceSuggestions}
              />
            ) : null}

            {tab === 'anamnesis' ? (
              <AnamnesisSection
                anam={anam}
                editable={editable}
                onChange={(key, value) => setAnam({ ...anam, [key]: value })}
                onBlurSave={() => void onSaveAnamnesis()}
              />
            ) : null}

            {tab === 'exam' ? (
              <PhysicalExamSection
                exam={exam}
                editable={editable}
                onChange={(key, value) => setExam({ ...exam, [key]: value })}
                onBlurSave={() => void onSaveExam()}
              />
            ) : null}

            {tab === 'diagnoses' ? (
              <DiagnosesSection
                diagnoses={diagnoses}
                can_diag={can_diag}
                editable={editable}
                new_diag={newDiag}
                on_change_new_diag={(patch) => setNewDiag({ ...newDiag, ...patch })}
                on_add={() => void onAddDiagnosis()}
                on_remove={(id) => void onRemoveDiagnosis(id)}
                on_update={(id, patch) => void onUpdateDiagnosis(id, patch)}
              />
            ) : null}

            {tab === 'plan' ? (
              <PlanTreatmentSection
                plan={plan}
                editable={editable}
                onChange={(key, value) => setPlan({ ...plan, [key]: value })}
                onBlurSave={() => void onSavePlan()}
              />
            ) : null}

            {tab === 'prescriptions' ? (
              <PrescriptionsSection visitId={visit.id} canWrite={can_rx} editable={!locked} appendable={appendable} printBase={rx_print_base} />
            ) : null}

            {tab === 'labs' ? (
              <LabsSection
                visitId={visit.id}
                canWrite={can_labs}
                editable={!locked}
                appendable={appendable}
                printBase={clinical_print_patient}
                chiefComplaint={detail?.anamnesis?.chief_complaint ?? visit.chief_complaint ?? null}
                primaryDiagnosis={primary_diagnosis}
              />
            ) : null}

            {tab === 'imaging' ? (
              <ImagingSection
                visitId={visit.id}
                canWrite={can_imaging}
                editable={!locked}
                appendable={appendable}
                printBase={clinical_print_patient}
                chiefComplaint={detail?.anamnesis?.chief_complaint ?? visit.chief_complaint ?? null}
                primaryDiagnosis={primary_diagnosis}
              />
            ) : null}

            {tab === 'attachments' ? (
              <AttachmentsSection visitId={visit.id} canWrite={can_attach} writable={appendable} />
            ) : null}

            {tab === 'addendums' ? (
              <div className="space-y-2 rounded-md border p-3">
                {addendums.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('clinical.no_addendums')}</p>
                ) : (
                  <ul className="space-y-1">
                    {addendums.map((ad) => (
                      <li key={ad.id} className="rounded-md border px-2 py-1 text-sm">
                        <p className="text-muted-foreground text-xs">{new Date(ad.created_at).toLocaleString()}</p>
                        <p>{ad.content}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {ADDENDABLE.has(visit.status) && can_addendum ? (
                  <div className="space-y-2">
                    <textarea
                      className="border-input bg-background min-h-[70px] w-full rounded-md border px-3 py-2 text-sm"
                      rows={3}
                      value={addendum}
                      onChange={(e) => setAddendum(e.target.value)}
                      placeholder={t('clinical.new_addendum')}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void onAddAddendum()}
                      disabled={addendum.trim().length < 5}
                    >
                      {t('clinical.add_addendum')}
                    </Button>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-xs italic">
                    {t('clinical.addendum_after_close_hint')}
                  </p>
                )}
              </div>
            ) : null}
          </div>

          <ClinicalSummaryPanel
            weight_kg={summary_values.weight_kg}
            temp_c={summary_values.temp_c}
            heart_rate_bpm={summary_values.heart_rate_bpm}
            respiratory_rate_bpm={summary_values.respiratory_rate_bpm}
            body_condition_score={summary_values.body_condition_score}
            diagnoses_count={diagnoses.length}
            has_plan={summary_values.has_plan}
            prescriptions_active_count={prescriptions_active_count}
            studies_count={studies_count}
            attachments_count={attachments_count}
            className="lg:sticky lg:top-20 lg:self-start"
          />
        </div>
      </CardContent>

      <ClinicalBottomActionBar
        last_saved_at={lastSavedAt}
        editable={editable}
        saving={savingAll}
        on_save_section={active_section_save ? () => void active_section_save() : null}
        on_save_and_continue={onSaveAndContinue ? () => void onSaveAndContinue() : null}
      />

      {showHistory ? (
        <ClinicalHistoryModal pet_id={visit.pet_id} onClose={() => setShowHistory(false)} />
      ) : null}

      {showCloseModal ? (
        <CloseVisitValidationModal
          anamnesis_complete={anamnesis_status === 'complete'}
          exam_complete={exam_complete}
          diagnoses_complete={diagnoses.length > 0}
          plan_complete={plan_status !== 'pending'}
          needs_diagnosis_reason={diagnoses.length === 0}
          needs_exam_reason={!exam_complete}
          reason_diag={closeReasons.diag}
          reason_exam={closeReasons.exam}
          on_change_reason_diag={(v) => setCloseReasons({ ...closeReasons, diag: v })}
          on_change_reason_exam={(v) => setCloseReasons({ ...closeReasons, exam: v })}
          closing={closing}
          error={closeModalError}
          on_confirm={() => void onConfirmClose()}
          on_cancel={() => setShowCloseModal(false)}
        />
      ) : null}
    </Card>
  );
}
